//AccessControl.js
import React, { useState, useEffect, useMemo } from 'react';
import { Search, Plus, Edit2, Trash2, UserPlus, Eye, Clock, Shield, MapPin, ChevronDown, ChevronRight, X } from 'lucide-react';
import {
  getBusinesses,
  getUsers,
  getUser,
  inviteUser,
  updateUser,
  removeUser,
  getSites,
  getUserStats,
  formatUserForAPI,
  getErrorMessage,
  filterUsers
} from './accessControlUtils';
import InvitationManagement from '../invite/InvitationManagement';
import HolographicSecurityLoader from './AccessSpinner';
import { hasPermission } from '../../utils/rbacUtils';
import { fetchRBACWithCache } from '../../utils/rbacCache';
import { color } from 'd3';
import { useNavigate } from 'react-router-dom';
import Button from '../../ui-components/Button/Button';
import 'ui-library-aol/dist/style.css';
import './AccessControl.css';

import {
  updateUserActivity
} from '../access/accessControlUtils';

const AccessControl = () => {
  // State management
  const [users, setUsers] = useState([]);
  const [sites, setSites] = useState([]);
  const [stats, setStats] = useState({});
  const [businesses, setBusinesses] = useState([]);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [currentBusinessRole, setCurrentBusinessRole] = useState(null);
  const navigate = useNavigate();

  // SEPARATE DATA LOADING FROM ANIMATION DISPLAY
  const [dataLoading, setDataLoading] = useState(true);     // Tracks actual data loading
  const [showAnimation, setShowAnimation] = useState(true); // Controls animation display
  
  const [error, setError] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');

  // Add invitation tab state
  const [invitationTabOpen, setInvitationTabOpen] = useState(false);

  // Add window width state for responsive design
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // Add focus state for glassmorphism inputs
  const [focusedInput, setFocusedInput] = useState(null);

  // QuickMenu state
  const [activeMenuItem, setActiveMenuItem] = useState('analytics');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  
  // CONSOLIDATED RBAC STATE - Single source of truth
  const [rbacData, setRbacData] = useState({
    globalRole: '',
    businessRoles: [],
    isLoaded: false
  });

  // Use centralized RBAC caching service
  const fetchAndStoreRBAC = async (email) => {
    try {
      const rbacResponse = await fetchRBACWithCache(email);

      // Update state - single source of truth
      setRbacData({
        globalRole: rbacResponse.global_role,
        businessRoles: rbacResponse.business_roles || [],
        isLoaded: true
      });

      return rbacResponse;
    } catch (error) {
      console.error('Error fetching RBAC data:', error);
      // Fallback to localStorage if fetch fails
      const storedRole = localStorage.getItem('rbac_role') || '';
      let storedBusinessRoles = [];
      try {
        storedBusinessRoles = JSON.parse(localStorage.getItem('rbac_data')) || [];
      } catch {
        storedBusinessRoles = [];
      }

      setRbacData({
        globalRole: storedRole,
        businessRoles: storedBusinessRoles,
        isLoaded: true
      });
    }
  };

  // Use rbacData state instead of localStorage
  const role = rbacData.globalRole?.toLowerCase();
  const rbacBusinessRoles = rbacData.businessRoles;

  const filteredBusinesses = useMemo(() => {
    if (!businesses || businesses.length === 0 || !rbacData.isLoaded) return [];
    
    // If TLA, show all businesses
    if (role === 'tla') {
      return businesses;
    }
    
    // Otherwise, filter based on rbac_data
    if (!Array.isArray(rbacBusinessRoles) || rbacBusinessRoles.length === 0) {
      console.warn('No valid rbac_data found');
      return [];
    }
    
    // Get businesses where user has admin or supervisor role
    // Technicians shouldn't see businesses in Access Control
    const userBusinessesWithAccess = rbacBusinessRoles
      .filter(item => {
        const itemRole = item.role?.toLowerCase();
        return itemRole === 'admin' || itemRole === 'supervisor';
      })
      .map(item => item.business);
    
    return businesses.filter(business => 
      userBusinessesWithAccess.includes(business.name) || 
      userBusinessesWithAccess.includes(business.code) || 
      userBusinessesWithAccess.includes(business.id)
    );
  }, [businesses, role, rbacBusinessRoles, rbacData.isLoaded]);

  const getUserRoleForBusiness = (businessId) => {
    if (!businessId || !rbacData.isLoaded) return null;
    
    // If TLA, they have admin access to all businesses
    if (role === 'tla') return 'admin';
    
    // Find the user's role for this specific business
    const businessData = rbacBusinessRoles.find(item => {
      const business = businesses.find(b => b.id === businessId);
      return business && (
        item.business === business.name || 
        item.business === business.code || 
        item.business === business.id
      );
    });
    
    return businessData ? businessData.role.toLowerCase() : null;
  };  

  // Form states
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: 'technician',
    selectedSites: []
  });

  const roles = [
    { value: 'admin', label: 'Admin', description: 'Full access to all sites and user management' },
    { value: 'supervisor', label: 'Supervisor', description: 'Read-only access to all sites and user viewing' },
    { value: 'technician', label: 'Technician', description: 'Site-specific access for maintenance tasks' }
  ];

  // SINGLE useEffect for initialization
  useEffect(() => {
    const email = localStorage.getItem('email');
    
    if (!email) {
      window.location.href = '/#auth/signin';
      return;
    }

    const initializeData = async () => {
      try {
        setDataLoading(true);
        setError(null);
        
        // Fetch RBAC data first
        await fetchAndStoreRBAC(email);
        
        // Then get available businesses
        const businessesData = await getBusinesses();
        
        // Remove duplicates based on ID
        const uniqueBusinesses = businessesData.filter((business, index, self) => 
          index === self.findIndex(b => b.id === business.id)
        );
        
        setBusinesses(uniqueBusinesses);
        
      } catch (error) {
        console.error('Error loading initial data:', error);
        setError(getErrorMessage(error));
      } finally {
        setDataLoading(false);
      }
    };

    initializeData();
  }, []); // Empty dependency array - run once only

  // Auto-select business when filtered businesses are available
  useEffect(() => {
    if (filteredBusinesses.length > 0 && !selectedBusiness && rbacData.isLoaded) {
      setSelectedBusiness(filteredBusinesses[0].id);
    } else if (filteredBusinesses.length === 0 && businesses.length > 0 && rbacData.isLoaded) {
      // User doesn't have access to any businesses with proper role
      setError('You don\'t have admin or supervisor access to any businesses.');
    }
  }, [filteredBusinesses, selectedBusiness, businesses.length, rbacData.isLoaded]);

  // Load data when business selection changes
  useEffect(() => {
    if (selectedBusiness !== null) {
      loadBusinessData();
    }
  }, [selectedBusiness]);

  // Update current business role when selection changes
  useEffect(() => {    
    if (selectedBusiness && rbacData.isLoaded) {
      const businessRole = getUserRoleForBusiness(selectedBusiness);
      setCurrentBusinessRole(businessRole);
    }
  }, [selectedBusiness, businesses, rbacData]);

  // Auto-close invitation tab when no pending invitations
  useEffect(() => {
    if (stats.pendingInvitations === 0) {
      setInvitationTabOpen(false);
    }
  }, [stats.pendingInvitations]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const loadBusinessData = async () => {
    if (!selectedBusiness) return;
    
    try {
      setDataLoading(true);  // Use dataLoading
      setError(null);
      
      // Load all data for the selected business in parallel
      const [usersData, sitesData, statsData] = await Promise.all([
        getUsers(selectedBusiness),
        getSites(selectedBusiness),
        getUserStats(selectedBusiness)
      ]);
      
      setUsers(usersData);
      setSites(sitesData);
      setStats(statsData);

      const adjustedStats = {
        ...statsData,
        totalUsers: usersData.filter(user => user.role.toLowerCase() !== 'tla').length
      };
      setStats(adjustedStats);
      
    } catch (error) {
      console.error('Error loading business data:', error);
      setError(getErrorMessage(error));
    } finally {
      setDataLoading(false);  // Data loading complete
    }
  };

  const handleBusinessChange = (businessId) => {
    setSelectedBusiness(businessId);
  };

  // Filter users based on search and role filter
  const filteredUsers = filterUsers(users, searchTerm, filterRole);

  const formatLastActive = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  const handleOpenModal = (type, user = null) => {
    setModalType(type);
    setSelectedUser(user);
    setShowModal(true);
    setError(null);
    
    if (type === 'edit' && user) {
      setFormData({
        name: user.name,
        email: user.email,
        role: user.role,
        selectedSites: user.sites.includes('all') ? [] : user.sites
      });
    } else if (type === 'invite') {
      setFormData({
        name: '',
        email: '',
        role: 'technician',
        selectedSites: []
      });
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedUser(null);
    setFormData({ name: '', email: '', role: 'technician', selectedSites: [] });
    setError(null);
    setFocusedInput(null); // Reset focus state
  };

  const handleSiteToggle = (site) => {
    setFormData(prev => ({
      ...prev,
      selectedSites: prev.selectedSites.includes(site)
        ? prev.selectedSites.filter(s => s !== site)
        : [...prev.selectedSites, site]
    }));
  };

  const handleSubmit = async () => {
    try {
      setSubmitLoading(true);
      setError(null);
      
      const userData = formatUserForAPI(formData);
      
      if (modalType === 'invite') {
        const newInvitation = await inviteUser(userData, selectedBusiness);
      } else if (modalType === 'edit') {
        const updatedUser = await updateUser(selectedUser.id, userData, selectedBusiness);
        setUsers(prev => prev.map(user => 
          user.id === selectedUser.id ? updatedUser : user
        ));
      }
      
      // Refresh stats
      const statsData = await getUserStats(selectedBusiness);
      setStats(statsData);
      
      handleCloseModal();
    } catch (error) {
      console.error('Error submitting form:', error);
      setError(getErrorMessage(error));
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleRemoveUser = async () => {
    try {
      setSubmitLoading(true);
      setError(null);
      
      await removeUser(selectedUser.id, selectedBusiness);
      setUsers(prev => prev.filter(user => user.id !== selectedUser.id));
      
      // Refresh stats
      const statsData = await getUserStats(selectedBusiness);
      setStats(statsData);
      
      handleCloseModal();
    } catch (error) {
      console.error('Error removing user:', error);
      setError(getErrorMessage(error));
    } finally {
      setSubmitLoading(false);
    }
  };

  const getRoleClassName = (role) => {
    switch (role) {
      case 'tla': return 'role-tla';
      case 'admin': return 'role-admin';
      case 'supervisor': return 'role-supervisor';
      case 'technician': return 'role-technician';
      default: return 'role-default';
    }
  };

  const getStatusClassName = (status) => {
    if (status === 'active') {
      return 'status-active';
    } else if (status === 'Never Active') {
      return 'status-neveractive';
    } else {
      return 'status-inactive';
    }
  };

  // Responsive style helpers
  const isMobile = windowWidth <= 768;
  const isSmallMobile = windowWidth <= 480;

  // Show animation until BOTH data is loaded AND animation is complete
  if (showAnimation || dataLoading || !rbacData.isLoaded) {
    return (
      <>
        {/* Holographic Security Loader */}
        <HolographicSecurityLoader 
          onComplete={() => setShowAnimation(false)}
          duration={1000} // 1 second animation - you can adjust this
        />
        
        {/* Content Below Spinner - Blurred and Dimmed */}
        <div style={{
          filter: 'blur(8px)',
          opacity: 0.3,
          pointerEvents: 'none',
          position: 'relative',
          zIndex: 1
        }}>
          <div className={`access-control-container ${isMobile ? 'mobile' : sidebarCollapsed ? 'sidebar-collapsed' : 'sidebar-expanded'}`}>
            <div className="access-control-max-width">
              {/* Header */}
              <div className="access-control-header">
                <h1 className="access-control-title">Access Control</h1>
                <p className="access-control-subtitle">Manage user access and site permissions</p>
                
                {/* Business Selector - Only show if multiple businesses */}
                {filteredBusinesses.length > 1 && (
                  <div className="business-selector">
                    <label className="business-label">Business:</label>
                    <select
                      className="business-select"
                      value={selectedBusiness || ''}
                      onChange={(e) => handleBusinessChange(e.target.value)}
                    >
                      {filteredBusinesses.map(business => (
                        <option key={business.id} value={business.id}>
                          {business.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              {/* Error Display */}
              {error && (
                <div className="error-card">
                  {error}
                </div>
              )}

              {/* Controls */}
              <div className="glass-card">
                <div className={`controls-container ${isMobile ? 'mobile' : ''}`}>
                  {/* Row 1: Search */}
                  <div style={{ display: 'flex', flex: 1 }}>
                    <div className="search-container">
                      <Search className="search-icon" />
                      <input
                        type="text"
                        placeholder="Search users..."
                        className="search-input"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                  </div>

                  {/* Row 2: Role Filter */}
                  <div style={{ display: 'flex', flex: 1 }}>
                    <select
                      className="access-control-select"
                      value={filterRole}
                      onChange={(e) => setFilterRole(e.target.value)}
                    >
                      <option value="all">All Roles</option>
                      <option value="admin">Admin</option>
                      <option value="supervisor">Supervisor</option>
                      <option value="technician">Technician</option>
                    </select>
                  </div>

                  {/* Row 3: Invite Button */}
                  {(currentBusinessRole === 'admin' || role === 'tla') && (
                    <div style={{ display: 'flex', flex: 1 }}>
                      <Button
                        variant="secondary"
                        onClick={() => handleOpenModal('invite')}
                      >
                        <UserPlus style={{ width: '16px', height: '16px' }} />
                        Invite User
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              {/* Loading skeleton for table and stats */}
              <div className="table-container">
                <div className="table-wrapper">
                  <table className="access-control-table">
                    <thead className="table-header">
                      <tr>
                        <th className="table-th">User</th>
                        <th className="table-th">Role</th>
                        <th className="table-th">Sites Access</th>
                        <th className="table-th">Date Joined</th>
                        <th className="table-th">Status</th>
                        <th className="table-th">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* Loading Skeleton Rows */}
                      {[1, 2, 3, 4, 5].map(index => (
                        <tr key={index} className="table-tr">
                          <td className="table-td">
                            <div>
                              <div className="user-name loading-skeleton">Loading User Name</div>
                              <div className="user-email loading-skeleton email">loading@example.com</div>
                            </div>
                          </td>
                          <td className="table-td">
                            <span className="role-badge loading-skeleton">Loading Role</span>
                          </td>
                          <td className="table-td">
                            <div className="site-info">
                              <MapPin style={{width: '16px', height: '16px', color: '#4a5568'}} />
                              <span className="site-text loading-skeleton">Loading sites</span>
                            </div>
                          </td>
                          <td className="table-td">
                            <div className="site-info">
                              <Clock style={{width: '16px', height: '16px', color: '#4a5568'}} />
                              <span className="site-text loading-skeleton">Loading time</span>
                            </div>
                          </td>
                          <td className="table-td">
                            <span className="loading-skeleton">Loading</span>
                          </td>
                          <td className="table-td">
                            <div className="actions-container">
                              <button className="action-button" style={{opacity: 0.3}} disabled>
                                <Eye style={{width: '16px', height: '16px'}} />
                              </button>
                              <button className="action-button" style={{opacity: 0.3}} disabled>
                                <Edit2 style={{width: '16px', height: '16px'}} />
                              </button>
                              <button className="action-button" style={{opacity: 0.3}} disabled>
                                <Trash2 style={{width: '16px', height: '16px'}} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Stats - Loading State */}
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-content">
                    <div>
                      <p className="stat-label">Total Users</p>
                      <p className="stat-value loading-skeleton">Loading</p>
                    </div>
                    <div className="stat-icon users">
                      <MapPin style={{width: '16px', height: '16px', color: '#c4b5fd'}} />
                    </div>
                  </div>
                </div>
                
                <div className="stat-card">
                  <div className="stat-content">
                    <div>
                      <p className="stat-label">Pending Invitations</p>
                      <p className="stat-value loading-skeleton">Loading</p>
                    </div>
                    <div className="stat-icon invitations">
                      <Clock style={{width: '16px', height: '16px', color: '#fbbf24'}} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }
  return (
    <>
      <div className={`access-control-container ${isMobile ? 'mobile' : sidebarCollapsed ? 'sidebar-collapsed' : 'sidebar-expanded'}`}>
        <div className="access-control-max-width">
          {/* Header */}
          <div className="access-control-header">
            <h1 className="access-control-title">Access Control</h1>
            <p className="access-control-subtitle">Manage user access and site permissions</p>

            <div className="analytics-nav-container">
              <div className="analytics-nav-container-wrapper">
                <button
                  onClick={() => {
                    updateUserActivity(localStorage.getItem('email'));
                    navigate('/analytic?view=baseSuite');
                  }}
                  className="analytics-nav-btn"
                >
                  BASE SUITE
                </button>
                <button 
                  onClick={() => {
                    updateUserActivity(localStorage.getItem('email'));
                    navigate('/analytic?view=lprBaseSuite');
                  }}
                  className="analytics-nav-btn"              
                >
                  LPR
                </button>
                <button 
                  onClick={() => {
                    updateUserActivity(localStorage.getItem('email'));
                    navigate('/analytic?view=lprInfringement');
                  }}
                  className="analytics-nav-btn"
                >
                  LPR INFRINGEMENT
                </button>
                <button
                  onClick={() => {
                    updateUserActivity(localStorage.getItem('email'));
                    navigate('/analytic?view=reid');
                  }}
                  className="analytics-nav-btn"
                >
                  REID
                </button>
                <button
                  onClick={() => {
                    updateUserActivity(localStorage.getItem('email'));
                    navigate('/reporting');
                  }}
                  className="analytics-nav-btn"
                >
                  REPORTING
                </button>
                <button
                  onClick={() => {
                    navigate('/access');
                  }}
                  className="analytics-nav-btn"
                >
                  ACCESS CONTROL
                </button>
              </div>
            </div>

            {/* Business Selector - Only show if multiple businesses */}
            {filteredBusinesses.length > 1 && (
              <div className="business-selector">
                {/* <label className="business-label">Business:</label> */}
                <select
                  className="business-select"
                  value={selectedBusiness || ''}
                  onChange={(e) => handleBusinessChange(e.target.value)}
                >
                  {filteredBusinesses.map(business => (
                    <option key={business.id} value={business.id}>
                      {business.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Error Display */}
          {error && (
            <div className="error-card">
              {error}
            </div>
          )}

          {/* Controls */}
          <div className="glass-card">
            <div className={`controls-container ${isMobile ? 'mobile' : ''}`}>
              <div className="controls-row">
                {/* Search */}
                <div className="search-container">
                  <Search className="search-icon" />
                  <input
                    type="text"
                    placeholder="Search users..."
                    className="search-input"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>

                {/* Role Filter */}
                <select
                  className="access-control-select"
                  value={filterRole}
                  onChange={(e) => setFilterRole(e.target.value)}
                >
                  <option value="all">All Roles</option>
                  <option value="admin">Admin</option>
                  <option value="supervisor">Supervisor</option>
                  <option value="technician">Technician</option>
                </select>
              </div>
              
              {/* Invite User Button */}
              {(currentBusinessRole === 'admin' || role === 'tla') && (
                <Button
                  variant="secondary"
                  onClick={() => handleOpenModal('invite')}
                  style={{border : 'none'}}
                >
                  <UserPlus style={{width: '16px', height: '16px'}} />  
                   Invite User
                </Button>
              )}
            </div>
          </div>

          {/* Invitation Management Tab */}
          {(currentBusinessRole === 'admin' || role === 'tla') && stats.pendingInvitations > 0 && (
            <div className="invitation-tab-container">
              <div 
                className={`invitation-tab-header ${invitationTabOpen ? 'open' : ''}`}
                onClick={() => setInvitationTabOpen(!invitationTabOpen)}
              >
                <div className="invitation-tab-title">
                  {invitationTabOpen ? (
                    <ChevronDown style={{width: '20px', height: '20px'}} />
                  ) : (
                    <ChevronRight style={{width: '20px', height: '20px'}} />
                  )}
                  <span>Pending Invitations</span>
                  <span className="invitation-tab-badge">
                    {stats.pendingInvitations}
                  </span>
                </div>
              </div>
              <div className={`invitation-tab-content ${invitationTabOpen ? 'open' : ''}`}>
                <InvitationManagement selectedBusiness={selectedBusiness} />
              </div>
            </div>
          )}

          {/* Users Table */}
          <div className="table-container">
            <div className="table-wrapper">
              <table className="access-control-table">
                <thead className="table-header">
                  <tr>
                    <th className="table-th">User</th>
                    <th className="table-th">Role</th>
                    <th className="table-th">Sites Access</th>
                    <th className="table-th">Date Joined</th>
                    <th className="table-th">Status</th>
                    {(currentBusinessRole === 'admin' || role === 'tla') && (
                      <th className="table-th">Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers
                    .filter(user => user.role.toLowerCase() !== 'tla')
                    .map((user) => (
                    <tr key={user.id} className="table-tr">
                      <td className="table-td">
                        <div>
                          <div className="user-name">{user.name}</div>
                          <div className="user-email">{user.email}</div>
                        </div>
                      </td>
                      
                      <td className="table-td">
                        <span className={`role-badge ${getRoleClassName(user.role)}`}>
                          <Shield style={{width: '12px', height: '12px'}} />
                          {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                        </span>
                      </td>

                      <td className="table-td">
                        <div className="site-info">
                          <MapPin style={{width: '16px', height: '16px', color: '#d1d5db'}} />
                          <span className="site-text">
                            {user.sites.includes('all') ? 
                              (user.role === 'supervisor' ? 'All Sites (Read-only)' : 
                               user.role === 'tla' ? 'All Sites (TLA Access)' : 'All Sites') : 
                              `${user.sites.length} site${user.sites.length !== 1 ? 's' : ''}`}
                          </span>
                        </div>
                      </td>

                      <td className="table-td">
                        <div className="site-info">
                          <Clock style={{width: '16px', height: '16px', color: '#d1d5db'}} />
                          <span className="site-text">
                            {formatLastActive(user.last_active)}
                          </span>
                        </div>
                      </td>

                      <td className="table-td">
                        <span className={`${getStatusClassName(user.status)}`} style={{fontWeight: '500'}}>
                          {user.status.charAt(0).toUpperCase() + user.status.slice(1)}
                        </span>
                      </td>

                      {(currentBusinessRole === 'admin' || role === 'tla') && (
                        <td className="table-td">
                          <div className="actions-container">
                            <button
                              className="action-button view"
                              onClick={() => handleOpenModal('view', user)}
                              title="View Details"
                            >
                              <Eye style={{width: '16px', height: '16px'}} />
                            </button>
                            {(currentBusinessRole === 'admin' || role === 'tla') && (
                              <>
                                <button
                                  className="action-button edit"
                                  onClick={() => handleOpenModal('edit', user)}
                                  title="Edit User"
                                >
                                  <Edit2 style={{width: '16px', height: '16px'}} />
                                </button>
                                <button
                                  className="action-button delete"
                                  onClick={() => handleOpenModal('remove', user)}
                                  title="Remove Access"
                                >
                                  <Trash2 style={{width: '16px', height: '16px'}} />
                                </button>
                              </>
                            )}
                            {currentBusinessRole === 'supervisor' && (
                              <span className="view-only-text">View Only</span>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Stats */}
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-content">
                <div>
                  <p className="stat-label">Total Users</p>
                  <p className="stat-value">{stats.totalUsers || 0}</p>
                </div>
                <div className="stat-icon users">
                  <MapPin style={{width: '16px', height: '16px', color: '#c4b5fd'}} />
                </div>
              </div>
            </div>
            
            {stats.pendingInvitations > 0 && (
              <div className="stat-card">
                <div className="stat-content">
                  <div>
                    <p className="stat-label">Pending Invitations</p>
                    <p className="stat-value">{stats.pendingInvitations || 0}</p>
                  </div>
                  <div className="stat-icon invitations">
                    <Clock style={{width: '16px', height: '16px', color: '#fbbf24'}} />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Modal with completely separate CSS classes */}
        {showModal && (
          <div className={`access-control-modal-overlay ${isMobile ? 'mobile' : ''}`}>
            <div className={`access-control-modal ${isMobile ? 'mobile' : ''}`}>
              <div className="access-control-modal-header">
                <h2 className="access-control-modal-title">
                  {modalType === 'invite' && 'Invite New User'}
                  {modalType === 'edit' && 'Edit User'}
                  {modalType === 'view' && 'User Details'}
                  {modalType === 'remove' && 'Remove User Access'}
                </h2>
                <button
                  onClick={handleCloseModal}
                  className="access-control-modal-close"
                >
                  <X style={{width: '20px', height: '20px'}} />
                </button>
              </div>

              <div className={`access-control-modal-content ${isMobile ? 'mobile' : ''}`}>
                {/* Error Display in Modal */}
                {error && (
                  <div className="error-card">
                    {error}
                  </div>
                )}

                {(modalType === 'invite' || modalType === 'edit') && (
                  <div className="form-container">
                    <div className={`form-grid ${isSmallMobile ? 'small-mobile' : ''}`}>
                      <div className="form-group">
                        <label className="form-label">
                          Full Name
                        </label>
                        <input
                          type="text"
                          required
                          className={`form-input ${focusedInput === 'name' ? 'focused' : ''}`}
                          value={formData.name}
                          onChange={(e) => setFormData({...formData, name: e.target.value})}
                          onFocus={() => setFocusedInput('name')}
                          onBlur={() => setFocusedInput(null)}
                          placeholder="Enter full name"
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">
                          Email Address
                        </label>
                        <input
                          type="email"
                          required
                          className={`form-input ${focusedInput === 'email' ? 'focused' : ''}`}
                          value={formData.email}
                          onChange={(e) => setFormData({...formData, email: e.target.value})}
                          onFocus={() => setFocusedInput('email')}
                          onBlur={() => setFocusedInput(null)}
                          placeholder="Enter email address"
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        Role
                      </label>
                      <select
                        className={`access-control-select ${focusedInput === 'role' ? 'focused' : ''}`}
                        value={formData.role}
                        onChange={(e) => setFormData({...formData, role: e.target.value})}
                        onFocus={() => setFocusedInput('role')}
                        onBlur={() => setFocusedInput(null)}
                      >
                        {roles.map(role => (
                          <option key={role.value} value={role.value}>
                            {role.label} - {role.description}
                          </option>
                        ))}
                      </select>
                    </div>

                    {(formData.role !== 'admin' && formData.role !== 'supervisor' && formData.role !== 'tla') && (
                      <div className="form-group">
                        <label className="form-label">
                          Site Access
                        </label>
                        <div className={`site-selection ${isMobile ? 'mobile' : ''}`}>
                          {sites.map(site => (
                            <label key={site} className="checkbox-label">
                              <input
                                type="checkbox"
                                className="checkbox"
                                checked={formData.selectedSites.includes(site)}
                                onChange={() => handleSiteToggle(site)}
                              />
                              <span className="checkbox-text">{site}</span>
                            </label>
                          ))}
                        </div>
                        {formData.selectedSites.length === 0 && (
                          <p className="warning-text">
                            Please select at least one site for technician users.
                          </p>
                        )}
                      </div>
                    )}

                    <div className={`button-group ${isMobile ? 'mobile' : ''}`}>
                      <button
                        type="button"
                        onClick={handleCloseModal}
                        className={`cancel-button ${isMobile ? 'mobile' : ''}`}
                        disabled={submitLoading}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={((formData.role !== 'admin' && formData.role !== 'supervisor' && formData.role !== 'tla') && formData.selectedSites.length === 0) || submitLoading}
                        className={`submit-button ${isMobile ? 'mobile' : ''}`}
                      >
                        {submitLoading ? 'Processing...' : (modalType === 'invite' ? 'Send Invitation' : 'Update User')}
                      </button>
                    </div>
                  </div>
                )}

                {modalType === 'view' && selectedUser && (
                  <div className="form-container">
                    <div className={`view-details-grid ${isMobile ? 'mobile' : ''}`}>
                      <div className="details-section">
                        <h3 className="section-title">User Information</h3>
                        <div className="detail-item">
                          <span className="detail-label">Name:</span> <span className="detail-value">{selectedUser.name}</span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">Email:</span> <span className="detail-value">{selectedUser.email}</span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">Role:</span>
                          <span className={`role-badge ${getRoleClassName(selectedUser.role)}`} style={{marginLeft: '8px'}}>
                            {selectedUser.role.charAt(0).toUpperCase() + selectedUser.role.slice(1)}
                          </span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">Status:</span>
                          <span className={`${getStatusClassName(selectedUser.status)}`} style={{marginLeft: '8px'}}>
                            {selectedUser.status.charAt(0).toUpperCase() + selectedUser.status.slice(1)}
                          </span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">Date Joined:</span> <span className="detail-value">{formatLastActive(selectedUser.last_active)}</span>
                        </div>
                      </div>
                      <div className="details-section">
                        <h3 className="section-title">Site Access</h3>
                        {selectedUser.sites.includes('all') ? (
                          <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                            <p style={{
                              fontWeight: '500', 
                              color: selectedUser.role === 'supervisor' ? '#1e40af' : 
                                     selectedUser.role === 'tla' ? '#7c3aed' : '#059669',
                              margin: '0'
                            }}>
                              All Sites {selectedUser.role === 'supervisor' ? '(Read-only Access)' : 
                                        selectedUser.role === 'tla' ? '(TLA Access)' : '(Full Access)'}
                            </p>
                          </div>
                        ) : (
                          <div className="sites-container">
                            {selectedUser.sites.length > 0 ? (
                              selectedUser.sites.map(site => (
                                <div key={site} className="site-item">
                                  <MapPin style={{width: '12px', height: '12px', color: '#9ca3af'}} />
                                  <span style={{fontSize: '14px', color: 'white'}}>{site}</span>
                                </div>
                              ))
                            ) : (
                              <p style={{color: '#f59e0b', margin: '0'}}>No sites assigned</p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className={`button-group ${isMobile ? 'mobile' : ''}`}>
                      <button
                        onClick={handleCloseModal}
                        className={`cancel-button ${isMobile ? 'mobile' : ''}`}
                      >
                        Close
                      </button>
                      {currentBusinessRole === 'admin' && (
                        <button
                          onClick={() => handleOpenModal('edit', selectedUser)}
                          className={`submit-button ${isMobile ? 'mobile' : ''}`}
                        >
                          Edit User
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {modalType === 'remove' && selectedUser && (
                  <div className="remove-confirmation">
                    <div className="remove-icon">
                      <Trash2 style={{height: '24px', width: '24px', color: '#dc2626'}} />
                    </div>
                    <h3 className="remove-title">Remove User Access</h3>
                    <p className="remove-text">
                      Are you sure you want to remove access for <strong>{selectedUser.name}</strong>? 
                      This action cannot be undone and they will lose access to all assigned sites.
                    </p>
                    <div className={`remove-button-group ${isMobile ? 'mobile' : ''}`}>
                      <button
                        onClick={handleCloseModal}
                        className={`cancel-button ${isMobile ? 'mobile' : ''}`}
                        disabled={submitLoading}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleRemoveUser}
                        className={`remove-button ${isMobile ? 'mobile' : ''}`}
                        disabled={submitLoading}
                      >
                        {submitLoading ? 'Removing...' : 'Remove Access'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Loading Overlay for Submit Actions */}
        {submitLoading && (
          <div className="loading-overlay">
            <div>Processing request...</div>
          </div>
        )}
      </div>
    </>
  );
};

export default AccessControl;